import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CheckCircle, Shield, FileText, Award, Beaker, ArrowRight, AlertTriangle, BarChart3, FlaskConical, HeartPulse, Microscope, Globe } from 'lucide-react';
export function UkweliInstitute() {
  const certificationLevels = [{
    level: 'Level 1: Purity & Safety',
    description: 'Analytical chemistry testing to verify ingredient quality, absence of contaminants, and label accuracy.',
    icon: <Shield className="text-amber-600" size={24} data-id="element-2734" data-mp="3pmrzqsof03tx" />,
    color: 'bg-amber-50',
    border: 'border-amber-500'
  }, {
    level: 'Level 2: Mechanistic Plausibility',
    description: 'Laboratory studies and biomarker research demonstrating biological mechanisms of action.',
    icon: <Beaker className="text-amber-600" size={24} data-id="element-2735" data-mp="299l8wpc03frz" />,
    color: 'bg-amber-50',
    border: 'border-amber-500'
  }, {
    level: 'Level 3: Clinical Efficacy',
    description: 'Human trials confirming real-world effectiveness and validating health outcome claims.',
    icon: <HeartPulse className="text-amber-600" size={24} data-id="element-2736" data-mp="2znw9uunwnxmn" />,
    color: 'bg-amber-50',
    border: 'border-amber-500'
  }];
  const certificationBenefits = [{
    title: 'For Consumers',
    description: 'Make informed decisions based on evidence, not marketing hype.',
    icon: <CheckCircle className="text-teal-600" size={24} data-id="element-2737" data-mp="2bxaea2v385tj" />,
    benefits: ['Clear understanding of which products have been scientifically validated', 'Confidence in product safety and quality', 'Ability to distinguish between different levels of evidence', 'Protection from false or exaggerated claims']
  }, {
    title: 'For Manufacturers',
    description: 'Differentiate your products with independent scientific validation.',
    icon: <Award className="text-teal-600" size={24} data-id="element-2738" data-mp="1xykgsm4cw44n" />,
    benefits: ['Third-party validation of product quality and efficacy', 'Competitive advantage in a crowded marketplace', 'Access to research expertise and testing protocols', 'Guidance for product improvement and innovation']
  }, {
    title: 'For Healthcare Providers',
    description: 'Recommend products with confidence based on scientific evidence.',
    icon: <FileText className="text-teal-600" size={24} data-id="element-2739" data-mp="1xsa2uacont5p" />,
    benefits: ['Evidence-based criteria for product recommendations', 'Access to detailed research data and clinical findings', 'Clear communication tools for patient education', 'Reduced risk of recommending ineffective products']
  }];
  const marketChallenges = [{
    challenge: 'Lack of Regulation',
    description: "Supplements aren't subject to the same pre-market approval as pharmaceuticals, creating quality and safety concerns.",
    icon: <AlertTriangle className="text-red-600" size={24} data-id="element-2740" data-mp="2j4iqy3d5di19" />
  }, {
    challenge: 'Misleading Claims',
    description: 'Marketing often outpaces science, with exaggerated benefits and selective evidence presentation.',
    icon: <AlertTriangle className="text-red-600" size={24} data-id="element-2741" data-mp="2t1n4xfwua76z" />
  }, {
    challenge: 'Poor Quality Control',
    description: "Studies show many products don't contain what's listed on the label or include undisclosed ingredients.",
    icon: <AlertTriangle className="text-red-600" size={24} data-id="element-2742" data-mp="36tjpmyw6hetc" />
  }, {
    challenge: 'Confusing Information',
    description: 'Consumers struggle to evaluate scientific claims and distinguish between credible and dubious products.',
    icon: <AlertTriangle className="text-red-600" size={24} data-id="element-2743" data-mp="3grhffqtorjo2" />
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-2744" data-mp="1hfx2c61fujw1">
      <Header data-id="element-2745" data-mp="1mdymyveiir9m" />
      <main className="flex-grow" data-id="element-2746" data-mp="1mdghd85yn0p1">
        {/* Hero Section */}
        <section className="relative w-full bg-gray-50" data-id="element-2747" data-mp="1mft7i8lvghd6">
          <div className="absolute inset-0 overflow-hidden" data-id="element-2748" data-mp="3cu0qm67baozk">
            <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
            backgroundBlendMode: 'overlay'
          }} data-id="element-2749" data-mp="37w92ln9sfn0n" />
            <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 to-teal-600/20" data-id="element-2750" data-mp="2p6akeeca0dja" />
          </div>
          <div className="container mx-auto px-4 py-16 md:py-24 relative z-10" data-id="element-2751" data-mp="32xxqt6zxn2fs">
            <div className="max-w-3xl" data-id="element-2752" data-mp="2y5nyxlf9bsav">
              <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4" data-id="element-2753" data-mp="2y4waqvnzowum">
                Ukweli Institute
              </h1>
              <div className="w-20 h-1 bg-amber-600 mb-6" data-id="element-2754" data-mp="26ezuo0iae6sz"></div>
              <p className="text-lg md:text-xl text-gray-700 mb-8" data-id="element-2755" data-mp="26eri69q2t3ib">
                Bringing truth and transparency to wellness products through
                rigorous, independent certification and scientific validation.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-2756" data-mp="1md5c0abt3zfc">
                <a href="#certification-process" className="px-6 py-3 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors duration-200 shadow-sm" data-id="element-2757" data-mp="1megdg3hs4v3a">
                  Certification Process
                </a>
                <a href="#certified-products" className="px-6 py-3 bg-white text-amber-600 border border-amber-600 rounded-md hover:bg-gray-50 transition-colors duration-200" data-id="element-2758" data-mp="2jzm513dibqva">
                  Certified Products
                </a>
                <a href="https://ukweliinstitute.org" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-gray-50 text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-200 flex items-center" data-id="element-2759" data-mp="tnydhbccr9u3">
                  UkweliInstitute.org
                  <ArrowRight size={16} className="ml-2" data-id="element-2760" data-mp="2rjwu5edz508e" />
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* About Ukweli Institute */}
        <section className="py-16 bg-white" data-id="element-2761" data-mp="2mj92qrlu2hdn">
          <div className="container mx-auto px-4" data-id="element-2762" data-mp="2hn1vmvhpazu4">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2763" data-mp="2hjnvltwif2pu">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-2764" data-mp="2diu6wyb8tjvw">
                About Ukweli Institute
              </h2>
              <div className="w-20 h-1 bg-amber-600 mx-auto mb-6" data-id="element-2765" data-mp="28n5dzdtqb18k"></div>
              <p className="text-gray-600" data-id="element-2766" data-mp="23nsik4osklud">
                The Ukweli Institute was established to bring
                pharmaceutical-grade validation standards to the supplement and
                wellness industry.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-12" data-id="element-2767" data-mp="9ex1yrzczxbc">
              <div data-id="element-2768" data-mp="9cawcxw13rvr">
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2769" data-mp="edgqn05c7n7e">
                  Our Mission
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-2770" data-mp="j3ndovk7aznj">
                  "Ukweli" means "truth" in Swahili, reflecting our commitment
                  to bringing scientific truth to the wellness industry. Our
                  mission is to establish rigorous, evidence-based standards for
                  evaluating supplements and wellness products, helping
                  consumers make informed choices based on science rather than
                  marketing claims.
                </p>
                <p className="text-gray-600" data-id="element-2771" data-mp="5dbholkhwgr4">
                  Through our Ukweli Certified™ program, we provide
                  independent, third-party validation of product quality,
                  safety, and efficacy, creating a clear distinction between
                  products that work and those that don't.
                </p>
              </div>
              <div data-id="element-2772" data-mp="21twy7etmhho4">
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2773" data-mp="1wwwtb78iekyl">
                  Why Certification Matters
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-2774" data-mp="2bmuj1rm37ipw">
                  The supplement and wellness industry suffers from limited
                  regulation and oversight, resulting in a marketplace where
                  quality, safety, and efficacy vary dramatically between
                  products. Consumers and healthcare providers struggle to
                  identify which products deliver on their promises.
                </p>
                <p className="text-gray-600" data-id="element-2775" data-mp="2uiklffxxmh4b">
                  Our certification program bridges this gap by applying
                  pharmaceutical-grade research methodologies to evaluate
                  wellness products, creating transparency and accountability in
                  an industry that desperately needs it.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Market Challenges */}
        <section className="py-16 bg-gray-50" data-id="element-2776" data-mp="3bsxzng2egkah">
          <div className="container mx-auto px-4" data-id="element-2777" data-mp="3bw2ed7dhqyfe">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2778" data-mp="2x41v3szjxpem">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-2779" data-mp="323wl7ubi6aym">
                Market Challenges We Address
              </h2>
              <div className="w-20 h-1 bg-amber-600 mx-auto mb-6" data-id="element-2780" data-mp="2o3ddypev38xi"></div>
              <p className="text-gray-600" data-id="element-2781" data-mp="2t389555o6kod">
                The wellness industry faces several critical challenges that our
                certification program directly addresses.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-2782" data-mp="2j4iox5qawo4y">
              {marketChallenges.map((item, index) => <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-2783" data-mp="cg5ov0t0uxwk" data-mpi={index}>
                  <div className="flex items-start" data-id="element-2784" data-mp="1p6ikpsm0ikjg">
                    <div className="bg-red-50 p-3 rounded-full mr-4" data-id="element-2785" data-mp="1p4pgejyvgcem">
                      {item.icon}
                    </div>
                    <div data-id="element-2786" data-mp="1f8umutt40wx2">
                      <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2787" data-mp="1f9no5f9auu7t">
                        {item.challenge}
                      </h3>
                      <p className="text-gray-600" data-id="element-2788" data-mp="1k5cqtrgm8r8c">{item.description}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
        {/* Certification Process */}
        <section id="certification-process" className="py-16 bg-white" data-id="element-2789" data-mp="1xzvq3u19jemf">
          <div className="container mx-auto px-4" data-id="element-2790" data-mp="22xx4i1nquprk">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2791" data-mp="22wm328hrtu3m">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-2792" data-mp="mjnru4sv6pgk">
                Ukweli Certified™ Process
              </h2>
              <div className="w-20 h-1 bg-amber-600 mx-auto mb-6" data-id="element-2793" data-mp="rkl82bubd9ff"></div>
              <p className="text-gray-600" data-id="element-2794" data-mp="35pzygx82utwh">
                Our three-tier certification framework establishes clear
                standards for evaluating supplement quality, safety, and
                efficacy.
              </p>
            </div>
            <div className="space-y-6 max-w-4xl mx-auto" data-id="element-2795" data-mp="3epd6rmkgtcfm">
              {certificationLevels.map((level, index) => <div key={index} className={`${level.color} p-6 rounded-lg border-l-4 ${level.border}`} data-id="element-2796" data-mp="2n01ryljxh8uy" data-mpi={index}>
                  <div className="flex items-start" data-id="element-2797" data-mp="2i31li9j6243b">
                    <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-2798" data-mp="2vt52om36v7lw">
                      {level.icon}
                    </div>
                    <div data-id="element-2799" data-mp="2r0bz41jgi5be">
                      <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2800" data-mp="2r023nyfn75yb">
                        {level.level}
                      </h3>
                      <p className="text-gray-700" data-id="element-2801" data-mp="1zbiiw3t20s1t">{level.description}</p>
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="mt-12 max-w-3xl mx-auto text-center" data-id="element-2802" data-mp="78wbhw1lidjf">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6" data-id="element-2803" data-mp="7732wb7f9j8s">
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2804" data-mp="c0ecgk3mce4e">
                  Certification Transparency
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-2805" data-mp="3fgmjhul5lyuh">
                  All Ukweli Certified™ products receive a detailed report
                  showing exactly which levels of certification they've
                  achieved, with specific test results and research findings
                  publicly available.
                </p>
                <a href="#certified-products" className="inline-flex items-center text-amber-600 hover:text-amber-700 transition-colors duration-200 font-medium" data-id="element-2806" data-mp="3ue5f9gw91cbf">
                  View certified products
                  <ArrowRight size={16} className="ml-1" data-id="element-2807" data-mp="19phgjmrf537b" />
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* Testing Methodologies */}
        <section className="py-16 bg-gray-50" data-id="element-2808" data-mp="2p8p2ui737tc4">
          <div className="container mx-auto px-4" data-id="element-2809" data-mp="2kf42iji0un9r">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2810" data-mp="2kdsx6f8xh7gj">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-2811" data-mp="2ffztbsv3kpjf">
                Our Testing Methodologies
              </h2>
              <div className="w-20 h-1 bg-amber-600 mx-auto mb-6" data-id="element-2812" data-mp="2afliy460x3i8"></div>
              <p className="text-gray-600" data-id="element-2813" data-mp="26fbhn4t6f19p">
                We employ rigorous scientific methods across multiple
                disciplines to thoroughly evaluate products.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6" data-id="element-2814" data-mp="1wjhuyslqtre7">
              <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-2815" data-mp="3vvkkajh1tfzs">
                <div className="bg-amber-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-2816" data-mp="3qwzkum9mf0ce">
                  <FlaskConical className="text-amber-600" size={24} data-id="element-2817" data-mp="2ojuy7mmpt15n" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2818" data-mp="2sj3t4uthr52s">
                  Analytical Chemistry
                </h3>
                <p className="text-gray-600" data-id="element-2819" data-mp="2ek40zd6rs81v">
                  Advanced testing to verify ingredient identity, potency, and
                  purity, including HPLC, mass spectrometry, and microbial
                  analysis.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-2820" data-mp="29max4qsxvq4r">
                <div className="bg-amber-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-2821" data-mp="1vvfn3b6pf2zl">
                  <Microscope className="text-amber-600" size={24} data-id="element-2822" data-mp="20t7c9xfysb03" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2823" data-mp="1lw89cmw256by">
                  Laboratory Research
                </h3>
                <p className="text-gray-600" data-id="element-2824" data-mp="1qwm6vb018xvq">
                  In vitro and ex vivo testing to evaluate biological activity,
                  including cell culture studies, biofilm models, and microbiome
                  simulators.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-2825" data-mp="1h8gx7knhwgmz">
                <div className="bg-amber-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-2826" data-mp="1ca5kn25tfs12">
                  <BarChart3 className="text-amber-600" size={24} data-id="element-2827" data-mp="1m68p4eo542r6" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2828" data-mp="1m4frfovi8svb">
                  Clinical Evaluation
                </h3>
                <p className="text-gray-600" data-id="element-2829" data-mp="1uy296b2vnvli">
                  Review of human clinical trials, biomarker analysis, and
                  real-world effectiveness data to validate health outcome
                  claims.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Benefits of Certification */}
        <section className="py-16 bg-white" data-id="element-2830" data-mp="2c1rhocc72ok8">
          <div className="container mx-auto px-4" data-id="element-2831" data-mp="2gyhxqzo01k5n">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2832" data-mp="2gzavjb4zczbn">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-2833" data-mp="2lx2zj3iwgbw7">
                Benefits of Ukweli Certification
              </h2>
              <div className="w-20 h-1 bg-amber-600 mx-auto mb-6" data-id="element-2834" data-mp="2qqxg3y37kar2"></div>
              <p className="text-gray-600" data-id="element-2835" data-mp="2vn5rr1i75lth">
                Our certification program creates value for all stakeholders in
                the wellness ecosystem.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-2836" data-mp="34px3l2ezijln">
              {certificationBenefits.map((benefit, index) => <div key={index} className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow duration-300" data-id="element-2837" data-mp="39mx9ndbu3mcr" data-mpi={index}>
                  <div className="bg-teal-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-2838" data-mp="1fagwrtoj99yx">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-2839" data-mp="12ywlis1q7feo">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-2840" data-mp="y14x480aibf8">{benefit.description}</p>
                  <ul className="text-gray-600 space-y-2" data-id="element-2841" data-mp="t21ralchk167">
                    {benefit.benefits.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-2842" data-mp="1qnpp9aolarju" data-mpi={idx}>
                        <span className="text-teal-600 mr-2" data-id="element-2843" data-mp="1lpeemxqh0d0k">•</span>
                        <span data-id="element-2844" data-mp="1lo3b8z0xt79z">{item}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>
        </section>
        {/* Certified Products */}
        <section id="certified-products" className="py-16 bg-gray-50" data-id="element-2845" data-mp="5c7gsafn2op4">
          <div className="container mx-auto px-4" data-id="element-2846" data-mp="cl01r0lqqbk">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2847" data-mp="bthphw0mp38">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-2848" data-mp="q07aw9i3pet9">
                Ukweli Certified™ Products
              </h2>
              <div className="w-20 h-1 bg-amber-600 mx-auto mb-6" data-id="element-2849" data-mp="v0mjyovco05f"></div>
              <p className="text-gray-600" data-id="element-2850" data-mp="h0k9votaab0q">
                Browse products that have successfully completed our
                certification process.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm max-w-4xl mx-auto" data-id="element-2851" data-mp="18ri08m86hjj1">
              <div className="text-center py-8" data-id="element-2852" data-mp="1dqbcycfn56i5">
                <div className="bg-amber-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" data-id="element-2853" data-mp="1dozxhfcu2ab7">
                  <Globe className="text-amber-600" size={32} data-id="element-2854" data-mp="zyo3tc0lo3hy" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-2855" data-mp="13z6vn62ht9k5">
                  Certified Product Database Coming Soon
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-2856" data-mp="1wgstlin78bn5">
                  We're currently in the process of evaluating our first batch
                  of products. Our comprehensive database of Ukweli Certified™
                  products will launch in early 2024, providing detailed
                  information on certification levels, test results, and
                  research findings.
                </p>
                <a href="/contact" className="px-6 py-3 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors duration-200 shadow-sm inline-block" data-id="element-2857" data-mp="3lxoczany4lda">
                  Inquire About Certification
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* Apply for Certification */}
        <section className="py-16 bg-amber-600 text-white" data-id="element-2858" data-mp="3376vk28kdc78">
          <div className="container mx-auto px-4" data-id="element-2859" data-mp="339t1jxft3jnc">
            <div className="max-w-3xl mx-auto text-center" data-id="element-2860" data-mp="nf96odoy32g3">
              <h2 className="text-3xl font-bold mb-4" data-id="element-2861" data-mp="ihhj1vvc22hp">
                Apply for Ukweli Certification
              </h2>
              <p className="text-amber-100 mb-8" data-id="element-2862" data-mp="1h4nqs9kfin7q">
                Manufacturers: Demonstrate your commitment to quality and
                efficacy by submitting your products for Ukweli Certification.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-2863" data-mp="1q6wy6qkei0gw">
                <a href="/certification/apply" className="px-6 py-3 bg-white text-amber-600 rounded-md hover:bg-gray-100 transition-colors duration-200 shadow-sm" data-id="element-2864" data-mp="1l6j0o2gfe8x4">
                  Certification Application
                </a>
                <a href="/contact" className="px-6 py-3 bg-amber-700 text-white border border-amber-400 rounded-md hover:bg-amber-800 transition-colors duration-200" data-id="element-2865" data-mp="28wlp88l4swqz">
                  Contact Our Team
                </a>
                <a href="https://ukweliinstitute.org" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-white text-amber-600 border border-amber-400 rounded-md hover:bg-gray-100 transition-colors duration-200 flex items-center" data-id="element-2866" data-mp="2isosxivmt3g1">
                  UkweliInstitute.org
                  <ArrowRight size={16} className="ml-2" data-id="element-2867" data-mp="2niit4zlsdc73" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-2868" data-mp="2era3cddsr5k8" />
    </div>;
}